import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import DashboardLayout from '../../../../components/layout/DashboardLayout';
import EcfBadge, { ESTADO_BADGE, COMERCIAL_BADGE } from '../components/EcfBadge';
import { DOC_TYPE_LABEL } from '../catalogos';
import { ecfService, type EcfDoc, type EstadoDgii } from '../../../../services/ecf.service';
import { descargarPdf, generateEcfRiPdf } from '../../../../lib/ecfRI';
import { formatMoney } from '../../../../utils/numberFormat';
import { exportToExcel } from '../../../../utils/exportImportUtils';

/**
 * e-CF emitidos: cada comprobante que sale del sistema, con su estado real en la DGII.
 *
 * El filtro vive en la URL (`?estado=`) para que el panel pueda enlazar directo a "los
 * rechazados". El filtrado lo resuelve el servidor y no el navegador: el histórico crece sin
 * techo y traerse un año de facturación para filtrarlo en memoria no se sostiene.
 *
 * Las acciones por fila hablan con la DGII de verdad, así que son irreversibles: enviar consume
 * el e-NCF. Ver `backend/src/modules/ecf/emitted/emitted.service.ts`.
 */

const FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'aceptado', label: 'Aceptados' },
  { value: 'enproceso', label: 'En proceso' },
  { value: 'pendiente', label: 'Sin enviar' },
  { value: 'rechazado', label: 'Rechazados' },
  { value: 'anulado', label: 'Anulados' },
] as const;

export default function EcfEmitidosPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState<EcfDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const filter = searchParams.get('estado') || 'all';

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setRows(
        await ecfService.listEmitted(
          filter === 'all' ? {} : { estado: filter as EstadoDgii }
        )
      );
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'No se pudieron cargar los comprobantes');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (value: string) => {
    if (value === 'all') setSearchParams({}, { replace: true });
    else setSearchParams({ estado: value }, { replace: true });
  };

  /** Envía o reintenta un comprobante. Sustituye la fila con lo que devuelve el servidor. */
  const enviar = async (row: EcfDoc) => {
    setBusyId(row.id);
    try {
      const actualizado = await ecfService.sendEmitted(row.id);
      setRows((prev) => prev.map((r) => (r.id === row.id ? actualizado : r)));

      if (actualizado.status === 'rechazado') {
        toast.error(`${row.encf} rechazado: ${actualizado.dgii_message ?? 'sin detalle'}`);
      } else if (actualizado.status === 'pendiente') {
        toast.error(actualizado.dgii_message ?? `${row.encf} no se pudo enviar`);
      } else {
        toast.success(`${row.encf} enviado a la DGII (${actualizado.status})`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo enviar el comprobante');
    } finally {
      setBusyId(null);
    }
  };

  /** Consulta el veredicto de un comprobante que quedó en proceso. */
  const consultarEstado = async (row: EcfDoc) => {
    setBusyId(row.id);
    try {
      const todos = await ecfService.refreshEmitted(row.id);
      const actualizado = todos.find((d) => d.id === row.id);
      if (actualizado) {
        setRows((prev) => prev.map((r) => (r.id === row.id ? actualizado : r)));
        toast.success(
          actualizado.status === 'enproceso'
            ? `${row.encf} sigue en proceso en la DGII`
            : `${row.encf}: ${ESTADO_BADGE[actualizado.status]?.label ?? actualizado.status}`
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo consultar el estado');
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Representación impresa. Se arma con el mismo constructor que valida la DGII en el paso 6, a
   * partir del XML firmado que se guardó: es el único que lleva el código de seguridad del QR.
   */
  const imprimir = async (row: EcfDoc) => {
    setBusyId(row.id);
    try {
      const data = await ecfService.emittedRiData(row.id);
      const { blob, filename } = await generateEcfRiPdf(data);
      descargarPdf(blob, filename);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo generar la representación impresa');
    } finally {
      setBusyId(null);
    }
  };

  const descargarXml = async (row: EcfDoc) => {
    try {
      await ecfService.downloadEmittedXml(row.id, row.encf);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo descargar el XML');
    }
  };

  const handleExport = () => {
    try {
      exportToExcel(
        rows.map((r) => ({
          'e-NCF': r.encf,
          Tipo: `${r.doc_type} — ${DOC_TYPE_LABEL[r.doc_type] ?? ''}`,
          Cliente: r.customer_name ?? (r.doc_type === '32' ? 'Consumidor final' : ''),
          RNC: r.customer_rnc ?? '',
          Monto: r.total,
          'Estado DGII': ESTADO_BADGE[r.status]?.label ?? r.status,
          'Mensaje DGII': r.dgii_message ?? '',
          'Aprobación cliente': COMERCIAL_BADGE[r.commercial_status]?.label ?? '',
          Fecha: r.created_at,
          TrackID: r.track_id ?? '',
          'Código de seguridad': r.security_code ?? '',
        })),
        'ecf-emitidos'
      );
      toast.success('Exportación a Excel completada');
    } catch {
      toast.error('Error al exportar a Excel');
    }
  };

  const fecha = (iso: string) =>
    new Date(iso).toLocaleString('es-DO', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Encabezado */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">e-CF Emitidos</h1>
            <p className="text-gray-600">
              Comprobantes fiscales electrónicos enviados a la DGII y su estado
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={load}
              disabled={loading}
              className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-50"
            >
              <i className="ri-refresh-line mr-2"></i>
              Actualizar
            </button>
            <button
              onClick={handleExport}
              disabled={rows.length === 0}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <i className="ri-file-excel-2-line mr-2"></i>
              Exportar
            </button>
            <button
              onClick={() => navigate('/taxes/ecf')}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Volver
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-3.5 py-2 rounded-lg border text-sm font-medium transition-colors whitespace-nowrap ${
                filter === f.value
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loadError && (
          <div className="bg-red-50 text-red-700 border border-red-200 p-4 rounded-lg flex items-center justify-between gap-4">
            <span>
              <i className="ri-error-warning-line mr-2"></i>
              {loadError}
            </span>
            <button onClick={load} className="text-sm font-medium hover:underline whitespace-nowrap">
              Reintentar
            </button>
          </div>
        )}

        {/* Tabla */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Comprobantes emitidos
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({rows.length} {rows.length === 1 ? 'registro' : 'registros'})
              </span>
            </h3>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      e-NCF
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Monto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado DGII
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Aprob. cliente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rows.map((r) => {
                    const estado = ESTADO_BADGE[r.status] ?? ESTADO_BADGE.pendiente;
                    const comercial =
                      COMERCIAL_BADGE[r.commercial_status] ?? COMERCIAL_BADGE.pendiente;
                    const busy = busyId === r.id;

                    return (
                      <tr key={r.id} className="hover:bg-gray-50 align-top">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-semibold text-gray-900">
                          {r.encf}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className="font-medium">{r.doc_type}</span>
                          <span className="block text-xs text-gray-500">
                            {DOC_TYPE_LABEL[r.doc_type]}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {r.customer_name ?? (r.doc_type === '32' ? 'Consumidor final' : '—')}
                          {r.customer_rnc && (
                            <span className="block text-xs font-mono text-gray-500">
                              RNC {r.customer_rnc}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                          {formatMoney(r.total, 'RD$')}
                        </td>
                        <td className="px-6 py-4">
                          <EcfBadge variant={estado.variant}>{estado.label}</EcfBadge>
                          {r.dgii_message && (
                            <span className="block text-xs text-gray-500 mt-1 max-w-xs">
                              {r.dgii_message}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <EcfBadge variant={comercial.variant}>{comercial.label}</EcfBadge>
                          {r.commercial_reason && (
                            <span className="block text-xs text-gray-500 mt-1 max-w-xs">
                              {r.commercial_reason}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {fecha(r.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                          {busy ? (
                            <i className="ri-loader-4-line animate-spin text-gray-400"></i>
                          ) : (
                            <>
                              {(r.status === 'pendiente' || r.status === 'rechazado') &&
                                r.has_xml && (
                                  <button
                                    onClick={() => enviar(r)}
                                    className="text-blue-600 hover:text-blue-900"
                                    title="Enviar a la DGII"
                                  >
                                    <i className="ri-send-plane-line"></i>
                                  </button>
                                )}
                              {r.track_id && r.status === 'enproceso' && (
                                <button
                                  onClick={() => consultarEstado(r)}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="Consultar estado en la DGII"
                                >
                                  <i className="ri-refresh-line"></i>
                                </button>
                              )}
                              {r.has_xml && (
                                <button
                                  onClick={() => imprimir(r)}
                                  className="text-gray-600 hover:text-gray-900"
                                  title="Representación impresa (PDF)"
                                >
                                  <i className="ri-printer-line"></i>
                                </button>
                              )}
                              {r.has_xml && (
                                <button
                                  onClick={() => descargarXml(r)}
                                  className="text-gray-600 hover:text-gray-900"
                                  title="Descargar XML firmado"
                                >
                                  <i className="ri-file-code-line"></i>
                                </button>
                              )}
                              {!r.has_xml && (
                                <span
                                  className="text-xs text-red-600"
                                  title="Sin XML firmado guardado: hay que volver a emitir el comprobante"
                                >
                                  Sin XML
                                </span>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                        {filter === 'all'
                          ? 'Sin comprobantes. Aquí aparece cada e-CF que emite el sistema, con su estado en la DGII.'
                          : 'Sin comprobantes en este estado.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
